import * as React from 'react';
import Form, { FormComponentProps } from 'antd/lib/form';
import Modal from 'antd/lib/modal';
import Row from 'antd/lib/row';
import Col from 'antd/lib/col';
import Input from 'antd/lib/input';
import Button from 'antd/lib/button';
import message from 'antd/lib/message';
import Icon from 'antd/lib/icon';
import Radio from 'antd/lib/radio';
import withStyles from 'react-jss';
import { StylesProps } from 'src/Library/Types';
import { createAddress } from 'src/api/organisationProfiling';
import { useGenericState } from 'src/Library/UseGenericState';
import { LocationPreferences } from '../InquiryUtils';
import { isValidArray, equal } from 'src/Library/Helper';
import { GOOGLE_PLACES_API_URL } from 'src/globals';
import AddCustomCFSLocationModal from './AddCustomCFSLocationModal';
import { tokens } from 'src/design-system';

const styles = (theme) => ({
  newLocationModal: {
    '& .ant-modal-title': {
      fontSize: `${tokens.fontSize.h3}px`,
      color: '#444444',
      fontWeight: 'bold',
    },
    '& .ant-modal-body': {
      padding: '14px',
    },
  },
  rowDiv: {
    display: 'flex',
    alignItems: 'center',
    marginLeft: '14px',
    padding: 10,
    '& .ant-form-item': {
      margin: 0,
      padding: 0,
    },
    '& .ant-form-explain': {
      fontSize: tokens.fontSize.caption,
    },
  },
  title: {
    paddingTop: '5px',
    fontSize: `${tokens.fontSize.small}px`,
    color: '#262626',
    fontWeight: tokens.fontWeight.semibold,
  },
  googleMapDiv: {
    '& .gm-fullscreen-control': {
      display: 'none',
    },
    height: '360px',
    width: '100%',
  },
  locationInput: {
    border: '1px solid #CCCCCC',
    borderRadius: `${tokens.radius.md}px`,
    fontSize: `${tokens.fontSize.small}px`,
    width: '100%',
    height: '30px',
    '& :focus': {
      border: '1px solid #1890FF',
    },
  },
  autocompleteDropdown: {
    width: '100%',
    padding: '6px 0px',
    borderRadius: `${tokens.radius.md}px`,
    boxShadow: ' 0 2px 8px rgba(0, 0, 0, 0.15)',
    boxSizing: 'border-box',
    background: '#fff',
    marginBottom: '5px',
    maxHeight: '150px',
    zIndex: 1060,
    position: 'relative',
    border: '1px',
    overflow: 'auto',
    cursor: 'pointer',
  },
  autoCompleteItem: {
    padding: ' 4px',
    width: '100%',
    display: 'inline-block',
    '&:hover': {
      backgroundColor: '#fafafa',
      color: 'rgba(0, 0, 0, 0.65)',
      cursor: 'pointer',
      fontWeight: tokens.fontWeight.semibold,
    },
  },
  marker: {
    width: '16px',
    height: '16px',
    borderRadius: '50% 50% 50% 0',
    background: '#db3832',
    transform: 'rotate(-45deg)',
    animationDuration: '1s',
  },
  addCustom: {
    float: 'left',
    cursor: 'pointer',
    color: tokens.colors.primary,
    fontSize: `${tokens.fontSize.small}px`,
  },
  radioLocation: {
    width: '100%',
    height: '100%',
    borderRadius: `${tokens.radius.md}px`,
    paddingTop: '10px',
    paddingBottom: '12px',
    marginBottom: '6px',
  },
  savedLocations: {
    position: 'absolute',
    top: '40px',
  },
});

interface IProps extends StylesProps<ReturnType<typeof styles>>, FormComponentProps {
  visible: boolean;
  handleCancel: () => void;
  loadAddresses: () => void;
}

interface StateProps {
  location;
  latlng: {
    lat: number,
    lng: number
  };
  selectedLocation?: number;
  showCustomAddress?: boolean;
  mapLocation?;
  formattedAddress: string;
  loading?: boolean;
}

const { useEffect } = React;

let autocomplete;

declare let google: any;

const AddCfsLocationModal = (props) => {
  const {
    visible,
    handleCancel,
    classes,
    loadAddresses,
    form,
  } = props;

  const savedValues = JSON.parse(localStorage.getItem('searchedCFS') || '[]');

  const [state, setState] = useGenericState<StateProps>({
    location: '',
    latlng: {
      lat: savedValues[0]?.latLng?.lat || 28.6448,
      lng: savedValues[0]?.latLng?.lng || 77.216721,
    },
    formattedAddress: '',
  });

  const {
    location,
    latlng,
    selectedLocation,
    showCustomAddress,
    mapLocation,
    formattedAddress,
  } = state;

  useEffect(() => {
    if (!showCustomAddress) {
      document.getElementById('googleMapsAPI')?.remove();
      const script = document.createElement('script');
      script.src = GOOGLE_PLACES_API_URL;
      script.async = true;
      script.onload = getPlaces;
      script.id = 'googleMapsAPI';
      document.body.appendChild(script);
    }
  }, [showCustomAddress]);

  // For each place, get the icon, name and location.
  const getIcon = () => ({
    url: "https://shipsy-public-assets.s3-us-west-2.amazonaws.com/shipper-dashboard/icons/marker.png",
    size: new google.maps.Size(71, 71),
    origin: new google.maps.Point(0, 0),
    anchor: new google.maps.Point(17, 34),
    scaledSize: new google.maps.Size(25, 25),
  });

  const getPlaces = (savedPlace?) => {
    const input = document.getElementById('autocomplete');
    const options = {
      types: ['establishment'],
    };

    autocomplete = new google.maps.places.Autocomplete(input, options);

    const map = new google.maps.Map(document.getElementById("googleMap"), {
      center: { lat: latlng?.lat, lng: latlng?.lng },
      zoom: 1,
      mapTypeId: "roadmap",
      maxZoom: 18,
    });
  
    let markers: any[] = [];
    autocomplete.addListener("place_changed", () => {
      const place = autocomplete.getPlace();
      if (!place) {
        return;
      }
      // Clear out the old markers.
      markers.forEach((marker) => {
        marker.setMap(null);
      });
      markers = [];
      const bounds = new google.maps.LatLngBounds();
      if (!place.geometry) {
        console.log("Returned place contains no geometry");
        return;
      }
      handlePlaceChanged(place);
      // Create a marker for each place.
      markers.push(
        new google.maps.Marker({
          map,
          icon: getIcon(),
          title: place.name,
          label: place.name,
          position: place.geometry.location,
        })
      );

      if (place.geometry.viewport) {
        // Only geocodes have viewport.
        bounds.union(place.geometry.viewport);
      } else {
        bounds.extend(place.geometry.location);
      }
      map.fitBounds(bounds);
      return;
    });
    const bounds = new google.maps.LatLngBounds();
    markers.push(
      new google.maps.Marker({
        map,
        icon: getIcon(),
        title: savedPlace.name,
        label: savedPlace.name,
        position: savedPlace.location,
      })
    );
    if (savedPlace.viewport) {
      // Only geocodes have viewport.
      bounds.union(savedPlace.viewport);
    } else {
      bounds.extend(savedPlace.location);
    }
    map.fitBounds(bounds);
  }

  const handlePlaceChanged = (place) => {
    setState({
      location: `${place.name} ${place.formatted_address}`,
      formattedAddress: place.formatted_address,
      selectedLocation: 0,
    });

    const address : any = {};
    place.address_components && place.address_components.forEach((item) => {
      address[item.types[0]] = item.long_name;
    });

    const latLng = {
      lat: place?.geometry?.location?.lat() || latlng?.lat,
      lng: place?.geometry?.location?.lng() || latlng?.lng,
    };

    const mapLocation = {
      latLng,
      name: place.name,
      city: address.locality,
      state: address.administrative_area_level_1,
      country: address.country,
      pincode: address.postal_code,
      viewPort: place.geometry.viewport,
      location: place.geometry.location,
    }

    const savedData = setSavedArray({
      ...mapLocation,
      formattedAddress: place.formatted_address,
    }, savedValues);
    window.localStorage.setItem('searchedCFS', JSON.stringify(savedData));

    setState({ mapLocation });
  }

  const setSavedArray = (object, SavedValues) : void => {
    const filteredArray = SavedValues?.map(item => ({
      name: item.name,
      pincode: item.pincode,
      state: item.state,
      city: item.city,
      country: item.country,
      formattedAddress: item.formattedAddress,
    }));
    const filteredObj = {
      name: object.name,
      pincode: object.pincode,
      state: object.state,
      city: object.city,
      country: object.country,
      formattedAddress: object.formattedAddress,
    };
    const duplicateSearch = filteredArray.findIndex((item) => {
      return equal(item, filteredObj);
    });
    if (duplicateSearch !== -1) {
      SavedValues.splice(0, 0, SavedValues.splice(duplicateSearch, 1)[0]);
    } else {
      SavedValues.length >= 2 && SavedValues.pop();
      SavedValues.unshift(object);
    }
    return SavedValues;
  };

  const handleCustomUpdate = async (values) => {
    setState({ loading: true });
    let apiObject = values;
    let name = location.street1 || location.street2 || location.city || location.state;
    apiObject.location = LocationPreferences.CFS;
    apiObject.name = values.name || name;
    apiObject.locationType = 'WAREHOUSE';
    apiObject.addressTypes = ['STUFFING', 'DESTUFFING'];
    const response = await createAddress(apiObject);
    if (response.isSuccess) {
      message.success(`Address created successfully`);
      loadAddresses();
      handleCancel();
    } else {
      message.error(response.errorMessage);
    }
    setState({ loading: false });
  };

  const handleCustomSubmit = async (): Promise<void> => {
    form.validateFields(async (err, values) => {
      if (err) return;
      await handleCustomUpdate(values);
    });
  };

  const handleGoogleUpdate = async () => {
    setState({ loading: true });
    let apiObject = {
      location: LocationPreferences.CFS,
      name: mapLocation?.name,
      city: mapLocation?.city,
      countryName: mapLocation?.country,
      pincode: mapLocation?.pincode,
      locationType: 'WAREHOUSE',
      address: formattedAddress,
      addressTypes: ['STUFFING', 'DESTUFFING'],
    };
    const response = await createAddress(apiObject);
    if (response.isSuccess) {
      message.success(`Address created successfully`);
      loadAddresses();
      handleCancel();
    } else {
      message.error(response.errorMessage);
    }
    setState({ loading: false });
  }

  const handleGoogleSubmit = async (): Promise<void> => {
      await handleGoogleUpdate();
  };

  const renderCustomAddress = () => {
    return (
      <AddCustomCFSLocationModal form={form} />
    );
  };

  const renderSavedValues = () => {
    return (
      <div className={classes.savedLocations}>
        {isValidArray(savedValues) ? (
          <span style={{ fontSize: `${tokens.fontSize.small}px`, marginBottom: '10px' }}>
            Select CFS Location Address
          </span>
        ) : null}
        <Radio.Group
          value={selectedLocation}
          style={{ width: '100%', paddingRight: '11px' }}
          onChange={(e) => {
            const value = e.target.value;
            setState({ selectedLocation: value });
            setState({
              selectedLocation: value,
              latlng: {
                lat: savedValues[value]?.latLng?.lat || latlng?.lat,
                lng: savedValues[value]?.latLng?.lng || latlng?.lng,
              },
              location: `${savedValues[value]?.name} ${savedValues[value]?.formattedAddress}`,
              mapLocation: savedValues[value],
              formattedAddress: savedValues[value]?.formattedAddress,
            });
            getPlaces(savedValues[value]);
          }}
        >
          {savedValues?.map((loc, index) => {
            return (
              <Radio.Button
                className={classes.radioLocation}
                value={index}
                style={{ borderRadius: `${tokens.radius.md}px` }}
              >
                <div style={{ fontSize: `${tokens.fontSize.body}px`, fontWeight: 600 }}>
                  {loc.name || loc.city}
                </div>
                <div style={{ fontSize: `${tokens.fontSize.caption}px`, lineHeight: '12px' }}>
                  {loc.formattedAddress}
                </div>
              </Radio.Button>
            );
          })}
        </Radio.Group>
      </div>
    );
  };

  const renderGoogleLocation = () => {
    return (
      <>
        <Row gutter={24} className={classes.modalRow}>
          <Col span={10}>
          <Input.Search
            id="autocomplete"
            placeholder={'Search & Select City'}
            value={location}
            onChange={(e) => {
              setState({
                location: e.target.value,
              });
            }}
            style={{ width: '100%', marginRight: '4%' }}
          ></Input.Search>
            {renderSavedValues()}
          </Col>
          <Col span={14}>
            <div className={classes.googleMapDiv} id="googleMap">
            </div>
          </Col>
        </Row>
      </>
    );
  };

  return (
    <Modal
      className={classes.newLocationModal}
      width={770}
      title="CFS Location Details"
      visible={visible}
      style={{ padding: '0' }}
      onCancel={handleCancel}
      footer={[
        <span
          className={classes.addCustom}
          onClick={() => setState({ showCustomAddress: !showCustomAddress })}
        >
          <Icon
            type="plus"
            style={{
              height: '12px',
              width: '12px',
              margin: '4px 4px 0 0',
            }}
          />
          {showCustomAddress ? 'Search on Map' : 'Add Custom Address'}{' '}
        </span>,
        <a onClick={handleCancel} style={{ color: '#424242', marginRight: '16px' }}>
          Cancel
        </a>,
        <Button
          key="submit"
          type="primary"
          style={{
            background: tokens.colors.primary,
            border: tokens.colors.primary,
            color: tokens.colors.bgWhite,
          }}
          onClick={() => {
            showCustomAddress ? handleCustomSubmit() : handleGoogleSubmit()
          }}
        >
          Add
        </Button>,
      ]}
    >
      {showCustomAddress ? renderCustomAddress() : renderGoogleLocation()}
    </Modal>
  );
};

export default withStyles(styles)(Form.create()((React.memo(AddCfsLocationModal))));
